import { InfoIcon } from '@phosphor-icons/react';
import type { SalesOrderV3Detail } from '@erp/rpc';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'src/components/ui/tooltip';
import type { Totals } from '@/pages/authenticated/quote-builder/marginCalc';
import { computeMetricDisplay } from './metricCompute';

type LineItem = SalesOrderV3Detail['lineItems'][number];
type Metric = {
  key: string;
  label: string;
  tooltip: string;
  /** Custom-metric fields (absent for catalog metrics) — drive the computed value. */
  aggregation?: string;
  format?: string;
  decimals?: number;
  dataPath?: string;
  compute?: { left: string; op: string; right: string };
};

/**
 * Config-driven metrics row (spec §6.2). Renders the org's configured metrics;
 * each value is computed live from the line data, so it stays correct as lines
 * are edited. Each card carries an ⓘ tooltip with the metric definition.
 */
export function MetricsRow({
  metrics,
  lines,
  currency,
  totals,
  valueOverrides,
}: {
  metrics: Metric[];
  lines: LineItem[];
  currency?: string | null;
  /** Margin totals (cost-based metrics); undefined when margin data isn't loaded. */
  totals?: Totals;
  /** Per-metric display override (keyed by metric key) — used by the config
   *  editor's preview to show made-up sample data for custom metrics. */
  valueOverrides?: Record<string, string>;
}) {
  if (metrics.length === 0) return null;

  return (
    <div className="mb-3 flex flex-wrap gap-2">
      {metrics.map((metric) => (
        <div
          key={metric.key}
          className="min-w-[120px] flex-1 rounded-md bg-muted/50 px-3 py-2"
        >
          <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
            {metric.label}
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="text-muted-foreground/70 hover:text-foreground"
                  aria-label={`About ${metric.label}`}
                >
                  <InfoIcon className="size-3" />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[240px]">
                {metric.tooltip}
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="mt-0.5 text-base font-semibold tabular-nums">
            {valueOverrides?.[metric.key] ??
              computeMetricDisplay(metric, lines, currency, totals)}
          </div>
        </div>
      ))}
    </div>
  );
}
